import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { LoadingProductTileSkeleton } from "@faire/retailer-visitor-shared/components/LoadingProductTileSkeleton/LoadingProductTileSkeleton";
import { PromotedBadge } from "@faire/retailer-visitor-shared/features/ADS/PromotedBadge";
import { shouldShowPromotedBadge } from "@faire/retailer-visitor-shared/features/ADS/util";
import { getAltTag } from "@faire/retailer-visitor-shared/features/Marketplace/utils";
import { MarketplaceProduct } from "@faire/retailer-visitor-shared/features/MarketplaceItems/MarketplaceProduct/MarketplaceProduct";
import { useProductTileClickImpressions } from "@faire/retailer-visitor-shared/hooks/ProductTileImpression/useProductTileClickImpressions";
import { generateProductLinkPropsForLoggedOutRetailer } from "@faire/retailer-visitor-shared/lib/generateProductLinkPropsForLoggedOutRetailer";
import { isAdmin } from "@faire/retailer-visitor-shared/lib/isAdmin";
import { useIsLoggedInRetailer } from "@faire/retailer-visitor-shared/lib/isLoggedInRetailer";
import { brandPage } from "@faire/retailer-visitor-shared/paths/index";
import { ProductPagePaths } from "@faire/retailer-visitor-shared/paths/ProductPage";
import { useStrictLocalization } from "@faire/web--source/common/localization";
import { isPrerender } from "@faire/web--source/common/user-agent/isPrerender";
import { Column } from "@faire/web--source/slate/Layout";
import { getSpacing, Spacer } from "@faire/web--source/slate/spacing";
import { responsiveMediaQueries } from "@faire/web--source/slate/Theme/responsiveMediaQueries";
import { useEffectEvent } from "@faire/web--source/ui/hooks/useEffectEvent";
import { observer } from "mobx-react";
import React from "react";
import { useLocation } from "react-router-dom";
import styled from "styled-components";
var TileBase = function(param) {
    var product = param.product, tileData = param.tileData, title = param.title, pageType = param.pageType, filterKeys = param.filterKeys, category = param.category, showVerticalImagery = param.showVerticalImagery;
    var _tileData_product, _tileData_product1, _tileData_product2, _tileData_product3;
    var isLoggedInRetailer = useIsLoggedInRetailer();
    var _tileData_product_promoted_by;
    var showPromotedBadge = shouldShowPromotedBadge(tileData === null || tileData === void 0 ? void 0 : (_tileData_product = tileData.product) === null || _tileData_product === void 0 ? void 0 : _tileData_product.is_promoted, (_tileData_product_promoted_by = tileData === null || tileData === void 0 ? void 0 : (_tileData_product1 = tileData.product) === null || _tileData_product1 === void 0 ? void 0 : _tileData_product1.promoted_by) !== null && _tileData_product_promoted_by !== void 0 ? _tileData_product_promoted_by : []);
    var _useProductTileClickImpressions = useProductTileClickImpressions(), recordProductTileRightClickEvent = _useProductTileClickImpressions.recordProductTileRightClickEvent, recordProductTileMiddleClickEvent = _useProductTileClickImpressions.recordProductTileMiddleClickEvent;
    var strictLocalize = useStrictLocalization().strictLocalize;
    var location = useLocation();
    var getProductLinkPropsFromProductTile = function(productTile) {
        var product = productTile === null || productTile === void 0 ? void 0 : productTile.product;
        if (!(product === null || product === void 0 ? void 0 : product.token) || !productTile) {
            return undefined;
        }
        var productPageLink = ProductPagePaths.index(product.token, {
            refF: filterKeys,
            refC: category === null || category === void 0 ? void 0 : category.navigationPath,
            productOptionToken: productTile.selected_product_option_token,
            impressionId: product.impression_id
        });
        if (!isLoggedInRetailer && !isAdmin() && !isPrerender()) {
            return generateProductLinkPropsForLoggedOutRetailer({
                productToken: product.token,
                brandToken: product.brand_token,
                refF: filterKeys,
                refC: category === null || category === void 0 ? void 0 : category.navigationPath,
                productOptionToken: productTile.selected_product_option_token,
                location: location
            });
        }
        return {
            productUrl: productPageLink
        };
    };
    var getBrandUrlFromProductTile = function(brand) {
        if (!brand) {
            return undefined;
        }
        return brandPage(brand.token, {
            page: 1,
            refC: category === null || category === void 0 ? void 0 : category.navigationPath,
            refF: filterKeys,
            refP: (product === null || product === void 0 ? void 0 : product.token) ? [
                product.token
            ] : undefined,
            sortingHint: true,
            impressionId: product === null || product === void 0 ? void 0 : product.impression_id
        });
    };
    var handleOnAuxClick = useEffectEvent(function(event) {
        if (event.button === 1) {
            recordProductTileMiddleClickEvent();
        } else if (event.button === 2) {
            recordProductTileRightClickEvent();
        }
    });
    if (tileData === undefined) {
        return /*#__PURE__*/ _jsx(LoadingProductTileSkeleton, {});
    }
    return /*#__PURE__*/ _jsxs(TileWrapper, {
        $enableSearchResultCleanup: isLoggedInRetailer,
        onAuxClick: handleOnAuxClick,
        children: [
            /*#__PURE__*/ _jsx(MarketplaceProduct, {
                altTag: getAltTag({
                    title: title,
                    pageType: pageType,
                    productName: tileData === null || tileData === void 0 ? void 0 : (_tileData_product2 = tileData.product) === null || _tileData_product2 === void 0 ? void 0 : _tileData_product2.name
                }, strictLocalize),
                productTile: tileData,
                brand: tileData === null || tileData === void 0 ? void 0 : tileData.brand,
                generateProductLinkProps: function() {
                    return getProductLinkPropsFromProductTile(tileData);
                },
                generateBrandUrl: function() {
                    return getBrandUrlFromProductTile(tileData === null || tileData === void 0 ? void 0 : tileData.brand);
                },
                showVerticalImagery: showVerticalImagery,
                shouldShowPromotedBadge: showPromotedBadge,
                recordAdImpression: true
            }),
            /*#__PURE__*/ _jsx(MaybeShowLoggedOutPromotedBadge, {
                showPromotedBadge: showPromotedBadge,
                promotedBy: tileData === null || tileData === void 0 ? void 0 : (_tileData_product3 = tileData.product) === null || _tileData_product3 === void 0 ? void 0 : _tileData_product3.promoted_by
            })
        ]
    });
};
export var Tile = observer(TileBase);
var MaybeShowLoggedOutPromotedBadge = function(param) {
    var showPromotedBadge = param.showPromotedBadge;
    var isLoggedInRetailer = useIsLoggedInRetailer();
    if (isLoggedInRetailer) {
        return null;
    }
    if (showPromotedBadge) {
        return /*#__PURE__*/ _jsx(PromotedBadge, {});
    }
    return /*#__PURE__*/ _jsx(Spacer, {
        size: "3x"
    });
};
var TileWrapper = styled(Column).withConfig({
    componentId: "sc-67a915b2-0"
})([
    "min-width:0;width:100%;padding:",
    ";",
    "{padding:",
    ";}"
], function(param) {
    var $enableSearchResultCleanup = param.$enableSearchResultCleanup;
    return $enableSearchResultCleanup ? 0 : getSpacing("1x", 4, 0);
}, responsiveMediaQueries.tabletAndAbove, function(param) {
    var $enableSearchResultCleanup = param.$enableSearchResultCleanup;
    return $enableSearchResultCleanup ? 0 : getSpacing("1x", "1x", 0);
});
